/**
 * CashflowFcfe (R2-Punkt 4) — Free Cashflow to Equity je Quartal (D-052/D-053).
 *
 * Zeigt die FCFE-Bausteine (fcfe.lines) als Zeilen, Q1–Q4 als Spalten. Jede
 * Spalte trägt Status-Badge (closed/active/forecast) und — bei Prognose-Quartalen
 * (basis="vj12") — die Prognose-Kennzeichnung samt Tooltip. Der basis-Wert
 * "vj12" bleibt aus Kompatibilität der MARKER für Forecast-Quartale; die
 * BENENNUNG folgt seit D-078 der neuen Systematik: EBTDA = VJ 2025/4,
 * Bank-Tilgung = aktuelle Ist-Rate, GD/Aktivierungen/Ausschüttungen =
 * ereignis-/ist-basiert ohne Fortschreibung (D-057/D-078). Bei
 * r003_naeherung=true zusätzlich der Näherungs-Hinweis (R-003).
 *
 * HARTE null-Regel (SCHEMA_MAPPING §5): null → "—", NIE 0.
 */
import type { Fcfe, FcfeLine, QuarterKey } from '../types/snapshot';
import { QuarterStatusBadge } from './QuarterStatusBadge';
import { DASH, formatEur } from '../lib/format';
import { useJahre } from '../lib/jahre';

const LINE_LABELS: Record<FcfeLine, string> = {
  ebtda: 'EBTDA',
  aktivierung: 'Aktivierung (CapEx)',
  bank_auf: 'Bankdarlehen-Aufnahme',
  bank_rueck: 'Bankdarlehen-Tilgung',
  gd_auf: 'GD-Aufnahme',
  gd_rueck: 'GD-Rückführung',
  ausschuettung: 'Ausschüttung',
  fcf: 'Free Cashflow (FCF)',
  fcfe: 'FCF to Equity (FCFE)',
};

/** fcf/fcfe werden als Ergebniszeilen hervorgehoben. */
const SUBTOTAL: ReadonlySet<FcfeLine> = new Set(['fcf', 'fcfe']);

const QUARTER_COLS: { key: QuarterKey; label: string }[] = [
  { key: 'q1_2026', label: 'Q1' },
  { key: 'q2_2026', label: 'Q2' },
  { key: 'q3_2026', label: 'Q3' },
  { key: 'q4_2026', label: 'Q4' },
];

/**
 * Prognose-Basis der Forecast-Quartale — GETRENNT nach Baustein, weil sie es
 * seit D-057 auch in der Rechnung ist. Wortlaut bewusst eng an der QR-Fußnote
 * Z134 gehalten (lbu-standortreporting/scripts/lib/qr_gen.py, `foot`), damit
 * Dashboard und Excel dieselbe Systematik beschreiben.
 *
 * ACHTUNG Doppelpflege: Der Snapshot transportiert diesen Text NICHT, eine
 * echte gemeinsame Quelle mit Z134 gäbe es nur über ein neues Snapshot-Feld.
 * Bis dahin müssen beide Stellen zusammen geändert werden (docs/OFFENE_BEFUNDE.md).
 *
 * D-089: das Vorjahr kommt aus meta (useJahre), nicht mehr fest „2025".
 */
function prognoseTitle(vj: number): string {
  return (
    'Forecast-Quartale, nach Baustein getrennt (D-078, ersetzt D-052): ' +
    `EBTDA = VJ ${vj}/4 (Q-Soll). Bank-Tilgung = aktuelle IST-Rate ` +
    '(Ø der letzten 3 SuSa-Monate, nur Rückführung — Aufnahmen sind Ereignisse). ' +
    'GD / Aktivierungen / Ausschüttungen = ereignis-/ist-basiert, KEINE ' +
    'Fortschreibung (D-057/D-078: Aktivierungen werden nie prognostiziert).'
  );
}

/** Badge-Text (D-078): EBTDA aus VJ, Finanzierung aus der Ist-Rate, Rest nur Ist. */
const PROGNOSE_BADGE = 'Prognose (EBTDA: VJ/4 · Tilgung: Ist-Rate)';

export interface CashflowFcfeProps {
  fcfe?: Fcfe;
}

export function CashflowFcfe({ fcfe }: CashflowFcfeProps) {
  const { vj } = useJahre(); // Hook vor dem frühen return (Rules of Hooks)
  if (!fcfe) return null;
  const lines = fcfe.lines?.length ? fcfe.lines : (Object.keys(LINE_LABELS) as FcfeLine[]);

  return (
    <section aria-label="FCF / FCFE je Quartal" className="space-y-2">
      <h4 className="text-sm font-semibold text-text">Free Cashflow to Equity (FCF / FCFE)</h4>
      <div className="overflow-x-auto rounded-lg border border-border bg-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-muted">
              <th scope="col" className="px-4 py-2 font-medium">Baustein</th>
              {QUARTER_COLS.map(({ key, label }) => {
                const q = fcfe.quartale[key];
                const istPrognose = q?.basis === 'vj12';
                return (
                  <th key={key} scope="col" className="px-4 py-2 text-right font-medium">
                    <div className="flex flex-col items-end gap-1">
                      <span>{label}</span>
                      {q ? <QuarterStatusBadge status={q.status} /> : (
                        <span className="text-xs font-normal italic text-text-muted">{DASH}</span>
                      )}
                      {istPrognose && (
                        <span
                          title={prognoseTitle(vj)}
                          className="cursor-help rounded border border-border bg-surface-2 px-1 text-[10px] font-normal text-text-muted"
                        >
                          {PROGNOSE_BADGE}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {lines.map((ln) => {
              const isSub = SUBTOTAL.has(ln);
              return (
                <tr
                  key={ln}
                  className={`border-b border-border/60 last:border-0 ${isSub ? 'bg-surface-2 font-semibold' : ''}`}
                >
                  <th scope="row" className={`px-4 py-1.5 text-left ${isSub ? 'font-semibold' : 'font-normal'}`}>
                    {LINE_LABELS[ln] ?? ln}
                  </th>
                  {QUARTER_COLS.map(({ key }) => {
                    const q = fcfe.quartale[key];
                    return (
                      <td key={key} className="px-4 py-1.5 text-right tabular-nums">
                        {q ? formatEur(q[ln]) : DASH}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {fcfe.r003_naeherung && (
        <p className="text-xs font-medium text-text">
          Werte teilweise genähert: ein Stichtag fehlt (Monatslücke) — Δ/Zwölftelung
          daher nur genähert (R-003). Die Prognose-Quartale können der
          Tilgungsrichtung entgegenlaufen.
        </p>
      )}
    </section>
  );
}
